import React, { memo, useMemo, useCallback, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import { has, isEmpty, flatMap } from "lodash";
import Accordion from "@mui/material/Accordion";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Select, MenuItem } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import AccordionTitle from "./OSOAccordionTitle";
import SubAccordion from "../SubAccordion";
import SummaryCalculator from "../SummaryCalculator";
import ExportButton from "../../components/ExportButton";
import {
  MenuProps,
  multiplierOptions,
  multiplierMappings,
  DropdownSx,
  ENTITY_TYPES,
  getAdjQtyErrorScope,
} from "../../utils";
import useStableCallback from "../../hooks/useStableCallback";
import { setAdjQtyError, setPrevAdjQty } from "../../store/globalSlice";

const OSOAccordionSummarySx = {
  backgroundColor: "#F1F1F1",
  "& .MuiAccordionSummary-content": {
    justifyContent: "space-between",
    alignItems: "center",
    margin: "6px 0",
  },
};

const CommonBoxSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  margin: "0 5px",
};

const OSOAccordionContainerSx = { padding: 1, marginTop: 2 };

const OSOAccordionSx = { my: 1, borderRadius: 1 };

const OSOAccordionCountSx = { color: "text.secondary", fontWeight: "bold" };

const OSOAccordionMultiplierSx = { minWidth: 120, m: 0, p: 0 };

const OSOAccordionSummaryContainerSx = {
  display: "flex",
  alignItems: "center",
  mr: 1,
};

const OSOAccordionTransitionProps = { timeout: 0, unmountOnExit: true };

const SelectAllSx = { mb: 1 };

function OSOAccordion({
  children,
  viewBy,
  filterData,
  fetchedData,
  getProcessedData,
  updateSelections,
  serverSelections,
  selectAllAccordion,
  selectAllSubAccordion,
  globalMultiplierChange,
}) {
  const dispatch = useDispatch();
  const adjQtyError = useSelector((store) => store.global.adjQtyError);
  const prevAdjQty = useSelector((store) => store.global.prevAdjQty);
  const entityType = useSelector((store) => store.global.settings.entityType);
  const restrictedPogs = useSelector(
    (store) => store.global.settings.restrictedPogs
  );

  const data = useMemo(() => fetchedData, [fetchedData]);
  const [globalMultiplierVal, setGlobalMultiplierVal] = useState({});

  const getCount = useCallback(
    (key) => {
      const selectionCount = has(serverSelections, key)
        ? Object.keys(serverSelections[key])?.length
        : 0;
      return `(${selectionCount}/${data[key]?.length})`;
    },
    [data, serverSelections]
  );

  const handleProcessedRow = useCallback(
    (key) => (updatedRow, index) => {
      getProcessedData(key, updatedRow, index);
    },
    [getProcessedData]
  );

  const handleSelectAllSubAccordion = useStableCallback(
    (scope) => (event) => {
      event.stopPropagation();
      const { checked } = event.target;
      selectAllSubAccordion(scope, checked);
    },
    [selectAllSubAccordion]
  );

  const getSelectAll = useCallback(
    (key) => {
      const selectionCount = has(serverSelections, key)
        ? Object.keys(serverSelections[key]).length
        : 0;
      return data[key]?.length === selectionCount;
    },
    [data, serverSelections]
  );

  const handleControlClick = useCallback((event) => {
    event.stopPropagation();
  }, []);

  const handleGlobalMultiplierChange = useCallback(
    (scope) => (event) => {
      event.stopPropagation();
      const { value: multiplierValue } = event?.target;
      globalMultiplierChange(scope, event?.target?.value);
      setGlobalMultiplierVal((prevState) => ({
        ...prevState,
        [scope]: multiplierValue,
      }));
    },
    [globalMultiplierChange]
  );

  const isRestrictedPog = useCallback(
    (scope) => {
      let restricted = false;
      if (ENTITY_TYPES.orders === entityType) {
        restricted = has(restrictedPogs, scope)
          ? restrictedPogs[scope]?.length === data[scope]?.length
          : false;
      }
      if (ENTITY_TYPES.planogram === entityType) {
        if (!!adjQtyError) {
          const { scope: errorScope } = getAdjQtyErrorScope(adjQtyError, data);
          restricted = scope === errorScope;
        }
      }
      return restricted;
    },
    [adjQtyError, data, entityType, restrictedPogs]
  );

  const handleAccExpand = useStableCallback((scope) => (e, exp) => {
    if (adjQtyError && !exp) {
      const { scope: errorScope, pogIndex } = getAdjQtyErrorScope(
        adjQtyError,
        data
      );

      if (errorScope === scope) {
        getProcessedData(
          scope,
          { id: adjQtyError, adjqty: prevAdjQty },
          pogIndex
        );
        dispatch(setAdjQtyError(null));
        dispatch(setPrevAdjQty(null));
      }
    }
  });

  const isSelectAllDisabled = useMemo(() => {
    if (ENTITY_TYPES.orders === entityType) {
      let isDisabled = false;
      const scopeList = Object.keys(data);
      for (let i = 0; i < scopeList?.length; i++) {
        if (isRestrictedPog(scopeList[i])) {
          isDisabled = true;
        } else {
          isDisabled = false;
          break;
        }
      }
      return isDisabled;
    } else if (ENTITY_TYPES.planogram === entityType) {
      if (!!adjQtyError) {
        return true;
      }
    }
    return false;
  }, [data, entityType, isRestrictedPog, adjQtyError]);

  const memoizedAccordionList = useMemo(() => {
    const scopeList = Object.keys(data) ?? [];
    const list = [];
    for (let i = 0; i < scopeList.length; i++) {
      const scope = scopeList[i];
      list.push(
        <Accordion
          key={scope}
          disableGutters
          TransitionProps={OSOAccordionTransitionProps}
          sx={OSOAccordionSx}
          onChange={handleAccExpand(scope)}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${scope}-panel-content`}
            id={`${scope}-panel-header`}
            sx={OSOAccordionSummarySx}
          >
            <Box sx={OSOAccordionSummaryContainerSx}>
              <Checkbox
                key={getSelectAll(scope)}
                defaultChecked={getSelectAll(scope)}
                disabled={isRestrictedPog(scope)}
                onClick={handleSelectAllSubAccordion(scope)}
              />
              <AccordionTitle
                type={viewBy}
                currentDetails={data[scope][0] ?? {}}
                headerDetails={filterData}
              />
              &nbsp;
              <Typography sx={OSOAccordionCountSx}>
                {getCount(scope)}
              </Typography>
            </Box>
            {entityType === ENTITY_TYPES.planogram && (
              <Box sx={OSOAccordionSummaryContainerSx}>
                <FormControl sx={OSOAccordionMultiplierSx} size="small">
                  <InputLabel>Multiplier</InputLabel>
                  <Select
                    label="Multiplier"
                    MenuProps={MenuProps}
                    onChange={handleGlobalMultiplierChange(scope)}
                    onOpen={handleControlClick}
                    onClose={handleControlClick}
                    value={globalMultiplierVal[scope]}
                    disabled={adjQtyError}
                  >
                    {multiplierOptions.map((option) => (
                      <MenuItem sx={DropdownSx} key={option} value={option}>
                        {multiplierMappings[option]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </AccordionSummary>
          <AccordionDetails sx={{ padding: 0 }}>
            <SubAccordion
              data={data}
              scope={scope}
              getProcessedRow={handleProcessedRow(scope)}
              serverSelections={serverSelections[scope]}
              updateSelections={updateSelections}
            />
          </AccordionDetails>
        </Accordion>
      );
    }
    return list;
  }, [
    data,
    viewBy,
    getCount,
    filterData,
    entityType,
    getSelectAll,
    isRestrictedPog,
    serverSelections,
    updateSelections,
    handleProcessedRow,
    handleControlClick,
    globalMultiplierVal,
    handleSelectAllSubAccordion,
    handleGlobalMultiplierChange,
  ]);

  const isAllSelected = useMemo(() => {
    const entityList = Object.keys(data);
    let selectedCount = -1;
    if (!isEmpty(serverSelections) && Object.keys(serverSelections).length) {
      selectedCount = entityList.reduce((acc, entity) => {
        if (!isEmpty(serverSelections[entity])) {
          const selectedPogs = Object.keys(serverSelections[entity]);
          if (data[entity].length === selectedPogs?.length) {
            acc = acc + 1;
          } else {
            acc = acc - 1;
          }
        } else {
          acc = acc - 1;
        }
        return acc;
      }, 0);
    }
    return entityList.length === selectedCount;
  }, [serverSelections, data]);

  const excelData = useMemo(() => {
    return flatMap(data).reduce((acc, pog) => {
      return [...acc, ...pog?.subRows];
    }, []);
  }, [data]);

  return (
    <Paper id="acc-container" sx={OSOAccordionContainerSx}>
      <Box sx={CommonBoxSx}>
        <FormControlLabel
          control={
            <Checkbox
              disableRipple
              onClick={selectAllAccordion}
              checked={isAllSelected}
              disabled={isSelectAllDisabled}
            />
          }
          label={`Select All ${viewBy}`}
          sx={SelectAllSx}
        />
        <ExportButton
          body={excelData}
          isExportAll
          selections={serverSelections}
        />
      </Box>
      <SummaryCalculator
        viewBy={viewBy}
        pogDetails={data}
        selections={serverSelections}
      />
      {memoizedAccordionList}
      {children}
    </Paper>
  );
}

export default memo(OSOAccordion);
